<!-- components/dashboard/PlanejamentoSemanal.vue -->
<template>
  <div class="planejamento-semanal">
    <!-- Header com controles de semana -->
    <v-card class="mb-4" elevation="2">
      <v-card-title class="d-flex align-center bg-gradient-success text-white">
        <v-icon class="mr-2">mdi-calendar-week</v-icon>
        <div>
          <div class="text-h6">Planejamento Semanal de Produção</div>
          <div class="text-subtitle-2 opacity-90">
            Organize as ordens por semana de produção
          </div>
        </div>

        <!-- ✅ ADICIONAR NO TEMPLATE PARA DEBUG -->
        <v-btn @click="testarBuscarSetores" color="orange" class="ma-2" v-if="ordensDisponiveis.length > 0">
          🧪 Testar Busca Setores
        </v-btn>


        <v-spacer />
        <div class="d-flex align-center gap-2">
          <v-btn icon variant="text" color="white" @click="semanaAnterior" :disabled="!podeVoltarSemana">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-chip color="white" text-color="success" size="large" class="font-weight-bold">
            {{ formatarPeriodoSemana }}
          </v-chip>
          <v-btn icon variant="text" color="white" @click="proximaSemana">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </v-card-title>
    </v-card>

    <!-- Estatísticas da Semana -->
    <v-row class="mb-4">
      <v-col cols="12" md="3">
        <v-card variant="outlined" class="text-center pa-4">
          <div class="text-h4 primary--text">{{ totalOrdensAlocadas }}</div>
          <div class="text-caption">Ordens Alocadas</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card variant="outlined" class="text-center pa-4">
          <div class="text-h4 success--text">{{ capacidadeUtilizada }}%</div>
          <div class="text-caption">Capacidade Utilizada</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card variant="outlined" class="text-center pa-4">
          <div class="text-h4 warning--text">{{ ordensUrgentes }}</div>
          <div class="text-caption">Ordens Urgentes</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card variant="outlined" class="text-center pa-4">
          <div class="text-h4 error--text">{{ conflitosDetectados }}</div>
          <div class="text-caption">Conflitos</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Grid de Planejamento -->
    <v-row>
      <!-- Coluna de Ordens Disponíveis -->
      <v-col cols="12" lg="4">
        <v-card elevation="2" class="h-100">
          <v-card-title class="bg-grey-lighten-4 d-flex align-center">
            <v-icon class="mr-2">mdi-clipboard-list</v-icon>
            Ordens Disponíveis
            <v-spacer />
            <v-chip size="small" color="primary">{{ ordensDisponiveis.length }}</v-chip>
          </v-card-title>

          <!-- Filtros para ordens disponíveis -->
          <v-card-subtitle class="pa-3 pb-0">
            <v-text-field v-model="filtroOrdens" label="Filtrar ordens..." prepend-inner-icon="mdi-magnify"
              variant="outlined" density="compact" clearable hide-details />
          </v-card-subtitle>

          <v-card-text class="pa-2 ordens-disponiveis-container">

            <!-- ✅ SINTAXE NOVA (Vue 3): -->
            <draggable v-model="ordensDisponiveisFiltradas" :group="{ name: 'ordens', pull: 'clone', put: false }"
              :clone="clonarOrdem" class="ordens-disponiveis" :sort="false" itemKey="id">
              <template #item="{ element: ordem }">
                <OrdemMiniCard :ordem="ordem" :draggable="true" class="mb-2"
                  @selecionar="$emit('selecionar-ordem', ordem)" />
              </template>
            </draggable>

            <!-- Estado vazio -->
            <div v-if="ordensDisponiveisFiltradas.length === 0" class="text-center pa-4">
              <v-icon size="48" color="grey">mdi-clipboard-search</v-icon>
              <div class="text-body-2 grey--text mt-2">
                {{ filtroOrdens ? 'Nenhuma ordem encontrada' : 'Todas as ordens foram alocadas' }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Coluna do Planejamento Semanal -->
      <v-col cols="12" lg="8">
        <v-card elevation="2">
          <v-card-title class="bg-primary text-white d-flex align-center">
            <v-icon class="mr-2">mdi-calendar-range</v-icon>
            <div>
              <div class="text-h6">{{ formatarPeriodoSemana }}</div>
              <div class="text-subtitle-2 opacity-90">
                Semana {{ numeroSemana }} de {{ anoAtual }}
              </div>
            </div>
            <v-spacer />
            <v-btn color="white" variant="outlined" @click="salvarPlanejamento" :loading="salvandoPlanejamento"
              :disabled="!temAlteracoes">
              <v-icon left>mdi-content-save</v-icon>
              Salvar Planejamento
            </v-btn>
          </v-card-title>

          <v-card-text class="pa-0">
            <!-- Dias da Semana -->
            <div class="semana-grid">
              <div v-for="(dia, index) in diasSemana" :key="index" class="dia-coluna" :class="{
                'dia-hoje': ehHoje(dia.data),
                'dia-fim-semana': ehFimDeSemana(dia.data)
              }">
                <!-- Header do Dia -->
                <div class="dia-header">
                  <div class="text-subtitle-2 font-weight-bold">
                    {{ dia.nome }}
                  </div>
                  <div class="text-caption">
                    {{ formatarDia(dia.data) }}
                  </div>
                  <div class="mt-1 d-flex align-center justify-center gap-1">
                    <v-chip v-if="dia.ordens.length > 0" size="x-small" color="primary">
                      {{ dia.ordens.length }}
                    </v-chip>
                    <v-chip v-if="calcularCapacidadeDia(dia) > 100" size="x-small" color="error">
                      {{ calcularCapacidadeDia(dia) }}%
                    </v-chip>
                  </div>
                </div>

                <!-- Área de Drop para Ordens -->
                <div class="dia-conteudo">
                  <!-- POR ESTA (adicionando item-key): -->
                  <!-- ✅ SINTAXE NOVA: -->
                  <draggable v-model="dia.ordens" :group="{ name: 'ordens' }" class="drop-zone" :class="{
                    'drop-zone-empty': dia.ordens.length === 0,
                    'drop-zone-over-capacity': calcularCapacidadeDia(dia) > 100
                  }" @add="onOrdemAdicionada($event, dia)" @remove="onOrdemRemovida($event, dia)"
                    @change="onPlanejamentoAlterado" itemKey="id">
                    <template #item="{ element: ordem }">
                      <OrdemPlanejamentoCard :ordem="ordem" :dia="dia" @remover="removerOrdemDoDia"
                        @selecionar="$emit('selecionar-ordem', ordem)" class="mb-2" />
                    </template>

                    <template #footer>
                      <div v-if="dia.ordens.length === 0" class="drop-placeholder">
                        <v-icon size="32" color="grey-lighten-2">
                          mdi-plus-circle-outline
                        </v-icon>
                        <div class="text-caption grey--text">
                          Arraste ordens aqui
                        </div>
                      </div>
                    </template>
                  </draggable>

                </div>

                <!-- Footer do Dia com Capacidade -->
                <div class="dia-footer">
                  <v-progress-linear :model-value="Math.min(100, calcularCapacidadeDia(dia))"
                    :color="getCorCapacidade(calcularCapacidadeDia(dia))" height="4" rounded />
                  <div class="text-caption text-center mt-1">
                    {{ calcularCapacidadeDia(dia) }}% capacidade
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Alertas e Conflitos -->
    <v-card v-if="alertasPlanejamento.length > 0" class="mt-4" elevation="2">
      <v-card-title class="bg-warning text-white">
        <v-icon class="mr-2">mdi-alert</v-icon>
        Alertas do Planejamento
      </v-card-title>
      <v-card-text>
        <v-list>
          <v-list-item v-for="(alerta, index) in alertasPlanejamento" :key="index">
            <template v-slot:prepend>
              <v-icon :color="alerta.cor">{{ alerta.icone }}</v-icon>
            </template>
            <v-list-item-title>{{ alerta.titulo }}</v-list-item-title>
            <v-list-item-subtitle>{{ alerta.descricao }}</v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>

    <!-- Snackbar para notificações -->
    <v-snackbar v-model="mostrarNotificacao" :color="tipoNotificacao" :timeout="4000" location="top right">
      {{ mensagemNotificacao }}
      <template v-slot:actions>
        <v-btn variant="text" @click="mostrarNotificacao = false">
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </div>



  <!-- Modal de Seleção de Setores -->
  <!-- ✅ MODAL DE SETORES CORRIGIDO -->
  <v-dialog v-model="modalSetores.mostrar" fullscreen persistent>
    <v-card>
      <v-card-title class="text-h5 bg-primary text-white">
        🏭 Selecionar Setores para {{ modalSetores.dia?.nome }}
      </v-card-title>

      <v-card-subtitle class="pa-4">
        <div class="ordem-info">
          <h3>📋 Ordem #{{ modalSetores.ordem?.id_ordem }}</h3>
          <p><strong>Cliente:</strong> {{ modalSetores.ordem?.cliente }}</p>
          <p><strong>Produto:</strong> {{ modalSetores.ordem?.produto }}</p>
          <p><strong>Quantidade Total:</strong> {{ modalSetores.ordem?.quantidadeTotal }} un</p>
        </div>
      </v-card-subtitle>

      <v-card-text>
        <div class="setores-selection">
          <!-- Filtros -->
          <div class="d-flex align-center justify-space-between mb-3">
            <h4 class="mb-0">🔧 Setores disponíveis para esta ordem:</h4>

            <div class="d-flex">
              <v-select v-model="modalSetores.filtroStatus"
                :items="['Todos', 'Pendentes', 'Em andamento', 'Finalizados', 'Sem saldo']" density="compact"
                label="Filtrar por status" class="mr-2" style="min-width:150px" />

              <v-btn color="primary" variant="outlined" size="small" class="ml-2" @click="selecionarTodosSetores"
                :disabled="todosSetoresSelecionados || !setoresFiltradosDisponiveis.length">
                <v-icon left>mdi-checkbox-multiple-marked</v-icon>
                Selecionar todos
              </v-btn>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="modalSetores.carregandoSetores" class="text-center pa-4">
            <v-progress-circular indeterminate color="primary" />
            <div class="mt-2">Carregando setores da ordem...</div>
          </div>

          <!-- Grid de setores -->
          <div v-else>
            <div class="mb-2 text-subtitle-2">
              <span v-if="setoresFiltradosDisponiveis.length === 0" class="text-red">
                Nenhum setor encontrado com o filtro atual.
              </span>
              <span v-else>
                Exibindo {{ setoresFiltradosDisponiveis.length }} de {{ modalSetores.setoresDisponiveis.length }}
                setores
              </span>
            </div>

             <!-- ✅ GRID DE SETORES SIMPLIFICADO -->
            <div class="setores-grid-clean">
              <v-card v-for="setor in setoresFiltradosDisponiveis" :key="setor.id" :class="['setor-card-clean', {
                'selected': modalSetores.setoresSelecionados.includes(setor.id),
                'setor-disabled': !podeSelecionar(setor),
                'setor-sem-saldo': setor.saldo_disponivel === 0
              }]" @click="toggleSetor(setor.id, setor)" elevation="0" :disabled="!podeSelecionar(setor)">
                <!-- ✅ HEADER SIMPLIFICADO -->
                <div class="setor-header-clean">
                  <div class="header-main">
                    <v-checkbox :model-value="modalSetores.setoresSelecionados.includes(setor.id)"
                      @click.stop="toggleSetor(setor.id, setor)" :disabled="!podeSelecionar(setor)" color="primary"
                      hide-details density="compact" />

                    <div class="setor-info">
                      <h3 class="setor-titulo">{{ setor.nome }}</h3>
                      <div class="setor-tags">
                        <v-chip size="small" :color="getCorStatusSetor(setor.status_geral)" variant="flat">
                          {{ setor.status_geral || 'Pendente' }}
                        </v-chip>
                        <span class="setor-detalhes">{{ setor.parte }} • {{ setor.tamanho }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="status-badge"
                    :class="setor.previsao_falta_a_ser_produzida > 0 ? 'disponivel' : 'indisponivel'">
                    <v-icon size="16">
                      {{ setor.previsao_falta_a_ser_produzida > 0 ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                    </v-icon>
                    <span>{{ setor.previsao_falta_a_ser_produzida > 0 ? 'Disponível' : 'Sem saldo' }}</span>
                  </div>
                </div>

                <!-- ✅ CONTEÚDO SIMPLIFICADO -->
                <div class="setor-body-clean">
                  <!-- Responsável e Tempo -->
                  <div class="info-row-clean">
                    <div class="info-item-clean">
                      <v-icon size="16" color="primary">mdi-account</v-icon>
                      <span class="info-text">{{ setor.funcionario_atual || 'Não atribuído' }}</span>
                    </div>
                    <div class="info-item-clean">
                      <v-icon size="16" color="orange">mdi-clock</v-icon>
                      <span class="info-text">{{ setor.tempo_estimado || '0h' }}</span>
                    </div>
                  </div>

                  <!-- ✅ MÉTRICAS PRINCIPAIS (SÓ 3) -->
                  <div class="metricas-clean">
                    <div class="metrica-clean planejada">
                      <span class="metrica-label">Planejada</span>
                      <span class="metrica-valor">{{ setor.planejada_final || 0 }}</span>
                    </div>

                    <div class="metrica-clean produzida">
                      <span class="metrica-label">Produzida</span>
                      <span class="metrica-valor">{{ setor.produzida || 0 }}</span>
                    </div>

                    <div class="metrica-clean pendente">
                      <span class="metrica-label">Pendente</span>
                      <span class="metrica-valor">{{ setor.previsao_falta_a_ser_produzida || 0 }}</span>
                    </div>
                  </div>

                  <!-- ✅ SEÇÃO DE QUANTIDADE (SE SELECIONADO) -->
                  <div v-if="modalSetores.setoresSelecionados.includes(setor.id)" class="quantidade-clean">
                    <v-text-field :model-value="obterQuantidadeSetor(setor.id)"
                      @update:modelValue="definirQuantidadeSetor(setor.id, $event)" type="number"
                      label="Quantidade a planejar" :max="setor.previsao_falta_a_ser_produzida" :min="1" variant="outlined"
                      density="compact" hide-details="auto" @click.stop :rules="[
                        v => v > 0 || 'Quantidade deve ser maior que 0',
                        v => v <= setor.previsao_falta_a_ser_produzida || `Máximo ${setor.previsao_falta_a_ser_produzida}`
                      ]">
                      <template v-slot:append-inner>
                        <span class="limite-text">/ {{ setor.previsao_falta_a_ser_produzida }}</span>
                      </template>
                    </v-text-field>

                    <!-- Botões rápidos -->
                    <div class="botoes-rapidos">
                      <v-btn v-for="valor in [50, 100, 200]" :key="valor"
                        @click.stop="definirQuantidadeSetor(setor.id, Math.min(valor, setor.previsao_falta_a_ser_produzida))"
                        size="small" variant="outlined" :disabled="valor > setor.previsao_falta_a_ser_produzida">
                        {{ valor }}
                      </v-btn>
                      <v-btn @click.stop="definirQuantidadeSetor(setor.id, setor.previsao_falta_a_ser_produzida)" size="small"
                        color="primary" variant="flat">
                        MAX
                      </v-btn>
                    </div>
                  </div>

                  <!-- Mensagem de erro -->
                  <div v-if="!podeSelecionar(setor)" class="erro-message">
                    <v-alert type="warning" variant="tonal" density="compact">
                      {{ obterMensagemBloqueio(setor) }}
                    </v-alert>
                  </div>
                </div>

                <!-- ✅ FOOTER SIMPLIFICADO -->
                <div v-if="podeSelecionar(setor)" class="setor-footer-clean">
                  <v-btn :variant="modalSetores.setoresSelecionados.includes(setor.id) ? 'flat' : 'outlined'"
                    :color="modalSetores.setoresSelecionados.includes(setor.id) ? 'primary' : 'default'" size="small"
                    block @click.stop="toggleSetor(setor.id, setor)">
                    <v-icon size="16">
                      {{ modalSetores.setoresSelecionados.includes(setor.id) ? 'mdi-check' : 'mdi-plus' }}
                    </v-icon>
                    {{ modalSetores.setoresSelecionados.includes(setor.id) ? 'Selecionado' : 'Selecionar' }}
                  </v-btn>
                </div>
              </v-card>
            </div>

          </div>

          <!-- ✅ RESUMO DA SELEÇÃO MELHORADO -->
          <v-alert v-if="modalSetores.setoresSelecionados.length > 0" type="info" class="mt-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <strong>✅ Setores selecionados ({{ modalSetores.setoresSelecionados.length }}):</strong>
                <div class="text-caption mt-1">
                  Total planejado: {{ calcularTotalPlanejado() }} unidades
                </div>
              </div>
              <v-btn size="small" color="primary" variant="outlined" @click="ajustarQuantidadesTodosSetores">
                <v-icon size="small" class="mr-1">mdi-auto-fix</v-icon>
                Ajustar Todas xxx
              </v-btn>
            </div>

            <div class="selected-setores mt-3">
              <v-chip v-for="setorId in modalSetores.setoresSelecionados" :key="setorId" class="mr-1 mb-1" closable
                @click:close="toggleSetor(setorId, modalSetores.setoresDisponiveis.find(s => s.id === setorId))"
                color="primary" variant="outlined">
                {{ obterNomeSetor(setorId) }}: {{ obterQuantidadeSetor(setorId) }} un
              </v-chip>
            </div>
          </v-alert>

          <!-- Aviso se nenhum setor selecionado -->
          <v-alert v-else-if="!modalSetores.carregandoSetores" type="warning" class="mt-4">
            ⚠️ Selecione pelo menos um setor para continuar
          </v-alert>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-btn @click="fecharModalSetores" text>
          Cancelar
        </v-btn>
        <v-spacer />
        <v-btn @click="confirmarSelecaoSetores" color="primary"
          :disabled="modalSetores.setoresSelecionados.length === 0">
          Confirmar Seleção ({{ modalSetores.setoresSelecionados.length }} setores)
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>



</template>

<script>
import draggable from 'vuedraggable';
import OrdemMiniCard from './cards/OrdemMiniCard.vue';
import OrdemPlanejamentoCard from './cards/OrdemPlanejamentoCard.vue';
import { producaoService } from '@/services/producaoService';

export default {
  name: 'PlanejamentoSemanal',
  components: {
    draggable,
    OrdemMiniCard,
    OrdemPlanejamentoCard
  },
  emits: ['selecionar-ordem', 'salvar-planejamento'],
  props: {
    ordens: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      semanaAtual: new Date(),
      salvandoPlanejamento: false,
      diasSemana: [],
      ordensDisponiveis: [],
      filtroOrdens: '',
      planejamentoOriginal: null,

      // Sistema de notificações
      mostrarNotificacao: false,
      mensagemNotificacao: '',
      tipoNotificacao: 'success',   // Modal de seleção de setores
      // ✅ MODAL DE SELEÇÃO DE SETORES
      modalSetores: {
        mostrar: false,
        ordem: null,
        dia: null,
        setoresSelecionados: [],
        setoresDisponiveis: [], // ✅ DINÂMICO POR ORDEM
        carregandoSetores: false,
        filtroStatus: 'Todos',
        busca: '',
        quantidadesSetor: {}
      }


    }
  },
  computed: {
    formatarPeriodoSemana() {
      const inicio = this.getInicioSemana(this.semanaAtual);
      const fim = this.getFimSemana(this.semanaAtual);
      return `${inicio.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} - ${fim.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`;
    },

    numeroSemana() {
      return this.getNumeroSemana(this.semanaAtual);
    },

    anoAtual() {
      return this.semanaAtual.getFullYear();
    },

    podeVoltarSemana() {
      const hoje = new Date();
      const inicioSemanaAtual = this.getInicioSemana(this.semanaAtual);
      return inicioSemanaAtual >= hoje;
    },

    ordensDisponiveisFiltradas() {
      if (!this.filtroOrdens) return this.ordensDisponiveis;

      const termo = this.filtroOrdens.toLowerCase();
      return this.ordensDisponiveis.filter(ordem =>
        ordem.id.toString().includes(termo) ||
        ordem.cliente?.toLowerCase().includes(termo) ||
        ordem.produto?.toLowerCase().includes(termo)
      );
    },

    totalOrdensAlocadas() {
      return this.diasSemana.reduce((total, dia) => total + dia.ordens.length, 0);
    },

    capacidadeUtilizada() {
      const capacidadeTotal = this.diasSemana.length * 100; // 100% por dia
      const capacidadeUsada = this.diasSemana.reduce((total, dia) =>
        total + this.calcularCapacidadeDia(dia), 0
      );
      return Math.round((capacidadeUsada / capacidadeTotal) * 100);
    },

    ordensUrgentes() {
      return this.diasSemana.reduce((total, dia) => {
        return total + dia.ordens.filter(ordem => ordem.urgente).length;
      }, 0);
    },

    conflitosDetectados() {
      return this.alertasPlanejamento.filter(a => a.tipo === 'conflito').length;
    },

    alertasPlanejamento() {
      const alertas = [];

      // Verificar capacidade excedida
      this.diasSemana.forEach(dia => {
        const capacidade = this.calcularCapacidadeDia(dia);
        if (capacidade > 100) {
          alertas.push({
            tipo: 'capacidade',
            cor: 'error',
            icone: 'mdi-alert-circle',
            titulo: `${dia.nome} - Capacidade Excedida`,
            descricao: `${capacidade}% de capacidade alocada (máximo recomendado: 100%)`
          });
        }
      });

      // Verificar ordens urgentes não alocadas
      const urgentesNaoAlocadas = this.ordensDisponiveis.filter(o => o.urgente);
      if (urgentesNaoAlocadas.length > 0) {
        alertas.push({
          tipo: 'urgente',
          cor: 'warning',
          icone: 'mdi-fire',
          titulo: 'Ordens Urgentes Não Alocadas',
          descricao: `${urgentesNaoAlocadas.length} ordem(ns) urgente(s) ainda não foram alocadas`
        });
      }

      return alertas;
    },

    temAlteracoes() {
      if (!this.planejamentoOriginal) return false;

      const planejamentoAtual = this.gerarPlanejamentoAtual();
      return JSON.stringify(this.planejamentoOriginal) !== JSON.stringify(planejamentoAtual);
    },

    setoresFiltrados() {
      if (!this.modalSetores.setoresDisponiveis) return [];

      return this.modalSetores.setoresDisponiveis.filter(setor => {
        let passaFiltroStatus = true;
        if (this.modalSetores.filtroStatus === 'Pendentes') {
          passaFiltroStatus = setor.status_geral === 'pendente';
        } else if (this.modalSetores.filtroStatus === 'Finalizados') {
          passaFiltroStatus = setor.status_geral === 'finalizado';
        } else if (this.modalSetores.filtroStatus === 'Sem produção') {
          passaFiltroStatus = setor.status_geral === 'sem';
        }

        let passaBusca = true;
        if (this.modalSetores.busca) {
          passaBusca = setor.nome.toLowerCase().includes(this.modalSetores.busca.toLowerCase());
        }

        return passaFiltroStatus && passaBusca;
      });
    },

    // ✅ Setores filtrados e disponíveis para seleção
    setoresFiltradosDisponiveis() {
      return this.setoresFiltrados.filter(setor => this.podeSelecionar(setor));
    },

    // ✅ Verifica se todos os setores disponíveis estão selecionados
    todosSetoresSelecionados() {
      if (this.setoresFiltradosDisponiveis.length === 0) return false;

      return this.setoresFiltradosDisponiveis.every(setor =>
        this.modalSetores.setoresSelecionados.includes(setor.id)
      );
    }
  },
  watch: {
    ordens: {
      handler() {
        this.inicializarDados();
      },
      immediate: true
    },
    semanaAtual() {
      this.carregarPlanejamentoSemana();
    }
  },
  methods: {

    // ✅ SELECIONAR TODOS OS SETORES
    selecionarTodosSetores() {
      const setoresSelecionaveis = this.setoresFiltradosDisponiveis.filter(setor => {
        return this.podeSelecionar(setor);
      });

      this.modalSetores.setoresSelecionados = [];

      setoresSelecionaveis.forEach(setor => {
        this.modalSetores.setoresSelecionados.push(setor.id);

        // Quantidade padrão inteligente
        const quantidadeTotal = this.modalSetores.ordem?.previsao_falta_a_ser_produzida || 0;
        const quantidadePorSetor = Math.floor(quantidadeTotal / setoresSelecionaveis.length);
        const quantidade = Math.min(quantidadePorSetor, setor.previsao_falta_a_ser_produzida);


        //  Definir quantidade do setor
        const quantidadeBrunoPrevisao = setor.previsao_falta_a_ser_produzida;
        this.definirQuantidadeSetor(setor.id, quantidadeBrunoPrevisao);

      });

      this.$toast?.success?.(`✅ ${setoresSelecionaveis.length} setores selecionados`);
    },

    // ✅ AJUSTAR QUANTIDADES DE TODOS OS SETORES
    ajustarQuantidadesTodosSetores() {

      alert('Ajustar quantidades de todos os setores');
      const quantidadeTotal = this.modalSetores.ordem?.previsao_falta_a_ser_produzida || 0;
      const setoresSelecionados = this.modalSetores.setoresSelecionados.length;

      if (setoresSelecionados === 0) return;

      // Distribuir igualmente entre os setores
      const quantidadePorSetor = Math.floor(quantidadeTotal / setoresSelecionados);

      this.modalSetores.setoresSelecionados.forEach(setorId => {
        const setor = this.modalSetores.setoresDisponiveis.find(s => s.id === setorId);
        if (setor) {
          const quantidade = Math.min(quantidadePorSetor, setor.previsao_falta_a_ser_produzida);
          this.definirQuantidadeSetor(setorId, quantidade);
        }
      });

      this.$toast?.success?.('✅ Quantidades ajustadas automaticamente');
    },

    // ✅ CALCULAR TOTAL PLANEJADO
    calcularTotalPlanejado() {
      return this.modalSetores.setoresSelecionados.reduce((total, setorId) => {
        return total + this.obterQuantidadeSetor(setorId);
      }, 0);
    },

  
 
 
    // ✅ OBTER MENSAGEM DE BLOQUEIO (ÚNICO)
    obterMensagemBloqueio(setor) {
      if (['FINALIZADO', 'FINALIZADO ERRO'].includes(setor.nome)) {
        return 'Setor já finalizado';
      }

      if (!setor.previsao_falta_a_ser_produzida || setor.previsao_falta_a_ser_produzida <= 0) {
        return 'Sem saldo disponível';
      }

      const statusLower = setor.status_geral?.toLowerCase() || '';
      if (statusLower.includes('finalizado')) {
        return 'Processo finalizado';
      }

      return 'Não disponível';
    },


    // ✅ MÉTODO CHAMADO QUANDO ORDEM É ARRASTADA PARA UM DIA
    async onOrdemAdicionada(evt, dia) {
      // console.log('📅 Ordem adicionada ao dia:', dia.nome);

      const ordemIndex = evt.newIndex;
      const ordem = dia.ordens[ordemIndex];

      if (!ordem) {
        console.error('❌ Ordem não encontrada');
        return;
      }

      // Remover a ordem temporariamente (será adicionada após confirmação)
      dia.ordens.splice(ordemIndex, 1);

      // Abrir modal de seleção de setores
      this.abrirModalSetores(ordem, dia);
    },

    // ✅ ABRIR MODAL DE SELEÇÃO DE SETORES
    // ✅ BUSCAR SETORES DA ORDEM ESPECÍFICA
    // ✅ USAR O ID CORRETO PARA BUSCAR SETORES
    async abrirModalSetores(ordem, dia) {
      //console.log('🏭 Abrindo modal de setores');
      //console.log('📋 Ordem completa:', ordem);
      //console.log('🔍 ID da ordem:', ordem.id);
      //console.log('🔍 ID_ordem:', ordem.id_ordem);

      this.modalSetores = {
        mostrar: true,
        ordem: { ...ordem },
        dia: { ...dia },
        setoresSelecionados: [],
        setoresDisponiveis: [],
        carregandoSetores: true
      };

      try {
        // ✅ USAR O ID CORRETO (id da tabela ordem_producao)
        await this.carregarSetoresDaOrdem(ordem.id);

      } catch (error) {
        console.error('❌ Erro ao carregar setores:', error);
        this.mostrarNotificacaoErro('Erro ao carregar setores da ordem');
        this.modalSetores.mostrar = false;
      }
    },

    // ✅ NOVO MÉTODO PARA BUSCAR SETORES DA ORDEM
    // ✅ ADICIONAR MAIS DEBUG
    async carregarSetoresDaOrdem(ordemId) {
      try {
        //console.log('🔍 Carregando setores da ordem ID:', ordemId);

        const response = await producaoService.buscarSaldosPorOrdem(ordemId);

        console.log('📡 Response da API:', response);

        if (response.status === 'sucesso' && response.data && response.data.saldos) {
          //console.log('📊 Saldos encontrados:', response.data.saldos.length);

          // ✅ EXTRAIR SETORES ÚNICOS DOS SALDOS
          const setoresUnicos = new Map();

          response.data.saldos.forEach((saldo, index) => {
            // //console.log(`🔧 Processando setor ${index + 1}:`, {
            //   id: saldo.id_setor,
            //   nome: saldo.nome_setor,


            // });


            //   status: saldo.status_geral
            //   status: saldo.status_geral

            if (!setoresUnicos.has(saldo.id_setor)) {
              setoresUnicos.set(saldo.id_setor, {
                id: saldo.id_setor, // ID do setor
                nome: saldo.nome_setor, // Nome do setor
                funcionario_atual: saldo.funcionario_atual_nome, // Nome do funcionário atual
                funcionario_atual_id: saldo.funcionario_atual_id, // 👈 ADICIONADO: ID do funcionário atual
                ordem_exibicao: saldo.ordem_exibicao_tela, // Ordem de exibição na tela
                status_geral: saldo.status_geral, // Status geral do setor
                planejada_inicial: saldo.planejada_inicial, // Quantidade planejada inicial
                planejada_final: saldo.planejada_final, // Quantidade planejada final
                saldo_disponivel: saldo.saldo_disponivel_produzir, // Saldo disponível (redundante)
                saldo_disponivel_produzir: saldo.saldo_disponivel_produzir, // Saldo disponível para produzir
                produzida: saldo.produzida, // 👈 ADICIONADO: Quantidade produzida
                recebida_aguardando_aprovocao: saldo.recebida_aguardando_aprovocao, // 👈 ADICIONADO
                recebida_aprovada: saldo.recebida_aprovada, // 👈 ADICIONADO
                recebida_qts: saldo.recebida_qts, // 👈 ADICIONADO
                saidas: saldo.saidas, // 👈 ADICIONADO
                transito: saldo.transito, // 👈 ADICIONADO
                parte: saldo.parte, // 👈 ADICIONADO
                tamanho: saldo.tamanho, // 👈 ADICIONADO
                id_ordem_producao_status_cadastro: saldo.id_ordem_producao_status_cadastro, // 👈 ADICIONADO
                previsao_falta_a_ser_produzida: saldo.previsao_falta_a_ser_produzida, // 👈 ADICIONADO
                status_aprovacao_enviadas: saldo.status_aprovacao_enviadas, // 👈 ADICIONADO
                status_aprovacao_recebimento: saldo.status_aprovacao_recebimento, // 👈 ADICIONADO

                movimentacoes: saldo.movimentacoes, // 👈 ADICIONADO: array de movimentações completas

                tempo_estimado: this.calcularTempoEstimadoSetor(saldo), // Tempo estimado calculado
                capacidade_utilizada: this.calcularCapacidadeSetor(saldo) // Capacidade utilizada calculada
              });
            }

          });

          // ✅ CONVERTER PARA ARRAY E ORDENAR
          this.modalSetores.setoresDisponiveis = Array.from(setoresUnicos.values())
            .sort((a, b) => a.ordem_exibicao - b.ordem_exibicao);

          console.log('✅ Setores únicos processados:', this.modalSetores.setoresDisponiveis);

        } else {
          console.error('❌ Resposta inválida da API:', response);
          throw new Error('Dados de setores não encontrados');
        }

      } catch (error) {
        console.error('❌ Erro ao buscar setores:', error);
        throw error;
      } finally {
        this.modalSetores.carregandoSetores = false;
      }
    },


    // ✅ ADICIONAR MÉTODO DE TESTE
    async testarBuscarSetores() {
      try {
        console.log('🧪 Testando busca de setores...');

        // Pegar a primeira ordem disponível
        const primeiraOrdem = this.ordensDisponiveis[0];
        if (!primeiraOrdem) {
          console.error('❌ Nenhuma ordem disponível para teste');
          return;
        }

        console.log('📋 Testando com ordem:', primeiraOrdem);
        console.log('🔍 ID para busca:', primeiraOrdem.id);

        const response = await producaoService.buscarSaldosPorOrdem(88);
        console.log('📡 Resposta do teste:', response);

      } catch (error) {
        console.error('❌ Erro no teste:', error);
      }
    },
    // ✅ CALCULAR TEMPO ESTIMADO DO SETOR
    calcularTempoEstimadoSetor(saldo) {
      // Baseado na quantidade e complexidade
      const quantidade = saldo.planejada_final || 0;
      const tempoBase = this.getTempoBasePorSetor(saldo.nome_setor);
      const tempoTotal = (quantidade * tempoBase) / 60; // em horas

      return tempoTotal > 1
        ? `${Math.round(tempoTotal)}h`
        : `${Math.round(tempoTotal * 60)}min`;
    },

    // ✅ CALCULAR CAPACIDADE DO SETOR
    calcularCapacidadeSetor(saldo) {
      const produzida = saldo.produzida || 0;
      const planejada = saldo.planejada_final || 1;
      const percentual = Math.round((produzida / planejada) * 100);

      return `${percentual}%`;
    },

    // ✅ TEMPO BASE POR TIPO DE SETOR (em minutos por peça)
    getTempoBasePorSetor(nomeSetor) {
      const temposBase = {
        'PCP': 2,
        'ESTOQUE': 1,
        'RISCO': 5,
        'CORTE': 8,
        'SEPARAÇÃO': 3,
        'BORDADO': 15,
        'BORDADO PELO': 10,
        'PREPARAÇÃO': 12,
        'COSTURA - FARDAMENTO MALHA': 25,
        'ACABAMENTO': 8,
        'EMBALAGEM 2': 5,
        'RECUPERACAO': 20,
        'FINALIZADO ERRO': 0,
        'FATURAMENTO': 2,
        'FINALIZADO': 0
      };

      return temposBase[nomeSetor] || 10; // 10 min padrão
    },

    // ✅ OBTER QUANTIDADE DO SETOR
    obterQuantidadeSetor(setorId) {
      if (!this.modalSetores.quantidadesSetor) {
        this.modalSetores.quantidadesSetor = {};
      }
      return this.modalSetores.quantidadesSetor[setorId] || 0;
    },


// ✅ DEFINIR QUANTIDADE DO SETOR (Vue 3 Otimizado)
definirQuantidadeSetor(setorId, quantidade) {
  // Inicializar se ainda não existir
  if (!this.modalSetores.quantidadesSetor) {
    this.modalSetores.quantidadesSetor = {};
  }

  const qtd = parseInt(quantidade) || 0;
  const setor = this.modalSetores.setoresDisponiveis.find(s => s.id === setorId);

  if (setor) {
    // Validar limites: mínimo 0, máximo o saldo disponível
    const valorFinal = Math.min(Math.max(0, qtd), setor.previsao_falta_a_ser_produzida);
    
    // Vue 3: Atribuição direta (reativo)
    this.modalSetores.quantidadesSetor[setorId] = valorFinal;
    
    // Opcional: Log para debug
    console.log(`Setor ${setorId}: quantidade definida para ${valorFinal}`);
  } else {
    console.warn(`Setor ${setorId} não encontrado`);
  }
},

      // ✅ VERIFICAR SE PODE SELECIONAR O SETOR (CORRIGIDO)
      podeSelecionar(setor) {
      if (!setor) return false;

      // Não permitir seleção de setores finalizados
      if (['FINALIZADO', 'FINALIZADO ERRO'].includes(setor.nome)) {
        return false;
      }

      // Não permitir seleção de setores sem saldo disponível
      if (!setor.previsao_falta_a_ser_produzida || setor.previsao_falta_a_ser_produzida <= 0) {
        return false;
      }

      // Verificar status geral
      const statusLower = setor.status_geral?.toLowerCase() || '';
      if (statusLower.includes('finalizado')) {
        return false;
      }

      return true;
    },

    // ✅ OBTER NOME DO SETOR
    obterNomeSetor(setorId) {
      const setor = this.modalSetores.setoresDisponiveis.find(s => s.id === setorId);
      return setor ? setor.nome : `Setor ${setorId}`;
    },

    // ✅ MÉTODO DE ORDEM ADICIONADA (ÚNICO E CORRIGIDO)
    async onOrdemAdicionada(evt, dia) {
      console.log('📅 Ordem adicionada ao dia:', dia.nome);

      const ordemIndex = evt.newIndex;
      const ordem = dia.ordens[ordemIndex];

      if (!ordem) {
        console.error('❌ Ordem não encontrada');
        return;
      }

      // Remover a ordem temporariamente (será adicionada após confirmação)
      dia.ordens.splice(ordemIndex, 1);

      // Abrir modal de seleção de setores
      this.abrirModalSetores(ordem, dia);
    },


    // ✅ TOGGLE SETOR
    toggleSetor(setorId, setor) {
      if (!this.podeSelecionar(setor)) {
        return;
      }

      const index = this.modalSetores.setoresSelecionados.indexOf(setorId);

      if (index === -1) {
        // Adicionar à seleção
        this.modalSetores.setoresSelecionados.push(setorId);

        // Definir quantidade padrão inteligente
        const quantidadeTotal = this.modalSetores.ordem?.quantidadeTotal || 0;
        const quantidadePadrao = Math.min(
          Math.max(100, Math.floor(quantidadeTotal / 3)),
          setor.previsao_falta_a_ser_produzida
        );

        this.definirQuantidadeSetor(setorId, quantidadePadrao);
        this.$toast?.success?.(`✅ ${setor.nome} selecionado (${quantidadePadrao} un)`);
      } else {
        // Remover da seleção
        this.modalSetores.setoresSelecionados.splice(index, 1);
        this.definirQuantidadeSetor(setorId, 0);
        this.$toast?.info?.(`❌ ${setor.nome} removido`);
      }
    },



    // ✅ CONFIRMAR SELEÇÃO DE SETORES
    async confirmarSelecaoSetores() {
      if (this.modalSetores.setoresSelecionados.length === 0) {
        this.$toast?.warning?.('Selecione pelo menos um setor!');
        return;
      }

      try {
        const { ordem, dia, setoresSelecionados } = this.modalSetores;

        // Adicionar informações dos setores à ordem
        const ordemComSetores = {
          ...ordem,
          setores_agendados: setoresSelecionados.map(setorId => ({
            setor_id: setorId,
            setor_nome: this.getSetorNome(setorId),
            dia_agendado: dia.data,
            dia_nome: dia.nome
          })),
          dia_agendado: dia.data,
          status_planejamento: 'agendada'
        };

        // Encontrar o dia correto e adicionar a ordem
        const diaIndex = this.diasSemana.findIndex(d => d.data === dia.data);
        if (diaIndex !== -1) {
          this.diasSemana[diaIndex].ordens.push(ordemComSetores);
        }

        // Remover da Lista de Ordens disponíveis
        this.removerOrdemDisponivel(ordem.id);

        // Salvar no backend
        await this.salvarAgendamento(ordemComSetores);

        // Fechar modal
        this.modalSetores.mostrar = false;

        this.$toast?.success?.(`✅ Ordem #${ordem.id_ordem} agendada para ${dia.nome} com ${setoresSelecionados.length} setores!`);

        console.log('✅ Ordem agendada com sucesso:', ordemComSetores);

      } catch (error) {
        console.error('❌ Erro ao confirmar agendamento:', error);
        this.$toast?.error?.('Erro ao agendar ordem');
      }
    },

    // ✅ CANCELAR SELEÇÃO
    cancelarSelecaoSetores() {
      console.log('❌ Cancelando seleção de setores');

      try {
        // Apenas atualizar as propriedades que precisamos, mantendo a estrutura original
        this.modalSetores.mostrar = false;
        this.modalSetores.setoresSelecionados = [];

        // Remover a ordem do dia caso estivesse em processo de alocação
        if (this.modalSetores.ordem && this.modalSetores.dia) {
          // Verificar se a ordem existe no dia e remover caso necessite
          const diaIndex = this.diasSemana.findIndex(d =>
            d.data.toDateString() === this.modalSetores.dia.data.toDateString());

          if (diaIndex !== -1) {
            // Procurar a ordem no dia
            const ordemIndex = this.diasSemana[diaIndex].ordens.findIndex(o =>
              o.id === this.modalSetores.ordem.id);

            // Se a ordem foi encontrada no dia, removê-la
            if (ordemIndex !== -1) {
              const ordemRemovida = this.diasSemana[diaIndex].ordens.splice(ordemIndex, 1)[0];
              console.log('❌ Ordem removida do dia após cancelamento:', ordemRemovida);

              // Devolver a ordem para a lista de disponíveis
              if (!this.ordensDisponiveis.find(o => o.id === ordemRemovida.id)) {
                this.ordensDisponiveis.push(ordemRemovida);
              }
            }
          }
        }

        console.log('✅ Seleção de setores cancelada com sucesso');
      } catch (error) {
        console.error('❌ Erro ao cancelar seleção de setores:', error);
      }
    },

    // ✅ FECHAR MODAL DE SETORES
    fecharModalSetores() {
      console.log('❌ Fechando modal de setores');
      this.cancelarSelecaoSetores();
    },

    // ✅ OBTER NOME DO SETOR
    getSetorNome(setorId) {
      const setor = this.modalSetores.setoresDisponiveis.find(s => s.id === setorId);
      return setor ? setor.nome : `Setor ${setorId}`;
    },

    // ✅ CONTAR SETORES POR STATUS
    contarSetoresPorStatus(status) {
      if (!this.modalSetores.setoresDisponiveis) return 0;
      return this.modalSetores.setoresDisponiveis.filter(setor => setor.status_geral === status).length;
    },

    // ✅ REMOVER ORDEM DA LISTA DISPONÍVEL
    removerOrdemDisponivel(ordemId) {
      const index = this.ordensDisponiveisFiltradas.findIndex(o => o.id === ordemId);
      if (index > -1) {
        this.ordensDisponiveisFiltradas.splice(index, 1);
      }
    },

    // ✅ SALVAR AGENDAMENTO NO BACKEND
    async salvarAgendamento(ordemComSetores) {
      try {
        console.log('💾 Salvando agendamento:', ordemComSetores);

        const dadosAgendamento = {
          ordem_id: ordemComSetores.id,
          dia_agendado: ordemComSetores.dia_agendado,
          setores: ordemComSetores.setores_agendados.map(s => ({
            setor_id: s.setor_id,
            setor_nome: s.setor_nome,
            quantidade: s.quantidade || 1,
            id_cadastro_status: s.id_cadastro_status || 1
          })),
          usuario: 'usuario_atual', // TODO: pegar do contexto
          observacoes: `Agendado via dashboard para ${ordemComSetores.setores_agendados.length} setores`
        };

        // Chamar o service para salvar
        const response = await producaoService.salvarPlanejamentoSemanal(dadosAgendamento);

        if (response.status !== 'sucesso') {
          throw new Error(response.message || 'Erro ao salvar agendamento');
        }

        console.log('✅ Agendamento salvo no backend');

      } catch (error) {
        console.error('❌ Erro ao salvar agendamento:', error);
        throw error;
      }
    },

    // SUBSTITUIR este método no seu componente:

    async inicializarDados() {
      this.loading = true
      try {
        console.log('🚀 Inicializando dados do planejamento...')

        // 🎯 USAR O SERVICE REAL
        await this.carregarOrdensDisponiveis()

        // Gerar dias da semana
        this.gerarDiasSemana()

        // Carregar planejamento existente da semana
        await this.carregarPlanejamentoSemana()

        console.log('✅ Dados inicializados com sucesso')

      } catch (error) {
        console.error('❌ Erro ao inicializar dados:', error)
        this.mostrarNotificacaoErro('Erro ao carregar dados iniciais')
      } finally {
        this.loading = false
      }
    },
    // ADICIONAR este método no seu componente:

    // ✅ CORRIGIR O MÉTODO carregarOrdensDisponiveis
    async carregarOrdensDisponiveis() {
      try {
        console.log('🔍 Carregando ordens disponíveis...');

        const chaveSemana = this.getChaveSemana();
        const response = await producaoService.buscarOrdensDisponiveis(chaveSemana);

        if (response.status === 'sucesso') {
          // ✅ MAPEAR CORRETAMENTE OS DADOS
          this.ordensDisponiveis = response.ordens.map(ordem => ({
            id: ordem.id, // ✅ ID da tabela ordem_producao
            id_ordem: ordem.id_ordem, // ✅ Número da ordem
            cliente: ordem.cliente,
            produto: ordem.produto,
            quantidadeTotal: ordem.quantidade_total,
            prioridade: ordem.prioridade,
            prazoEntrega: ordem.prazo_entrega,
            prazoEntregaFormatado: ordem.prazo_entrega_formatado,
            urgente: ordem.urgente,
            atrasada: ordem.atrasada || false,
            statusDashboard: ordem.status_dashboard,
            proximoSetor: ordem.proximo_setor, // ✅ Manter objeto completo
            tempoEstimado: ordem.tempo_estimado // ✅ Manter objeto completo
          }));

          //console.log(`✅ ${this.ordensDisponiveis.length} ordens carregadas`);
          //console.log('📋 Primeira ordem:', this.ordensDisponiveis[0]);

        } else {
          throw new Error(response.message || 'Erro ao carregar ordens');
        }

      } catch (error) {
        console.error('❌ Erro ao carregar ordens:', error);
        this.mostrarNotificacaoErro('Erro ao carregar ordens disponíveis');
        this.ordensDisponiveis = [];
      }
    },


    gerarDiasSemana() {
      const inicio = this.getInicioSemana(this.semanaAtual);
      this.diasSemana = [];

      for (let i = 0; i < 7; i++) {
        const data = new Date(inicio);
        data.setDate(inicio.getDate() + i);

        this.diasSemana.push({
          nome: data.toLocaleDateString('pt-BR', { weekday: 'short' }).toUpperCase(),
          data: new Date(data),
          ordens: []
        });
      }
    },

    atualizarOrdensDisponiveis() {
      const chaveSemana = this.getChaveSemana();
      this.ordensDisponiveis = this.ordens.filter(ordem =>
        !ordem.semanaAlocada || ordem.semanaAlocada !== chaveSemana
      );
    },

    async carregarPlanejamentoSemana() {
      try {
        const chaveSemana = this.getChaveSemana();
        const response = await producaoService.buscarPlanejamentoSemana(chaveSemana);

        if (response.status === 'sucesso' && response.planejamento) {
          this.aplicarPlanejamento(response.planejamento);
        } else {
          // Limpar planejamento se não existir
          this.diasSemana.forEach(dia => {
            dia.ordens = [];
          });
        }

        this.salvarPlanejamentoOriginal();
        this.atualizarOrdensDisponiveis();

      } catch (error) {
        console.error('Erro ao carregar planejamento:', error);
        this.mostrarNotificacaoErro('Erro ao carregar planejamento da semana');
      }
    },

    aplicarPlanejamento(planejamento) {
      if (!planejamento.dias) return;

      planejamento.dias.forEach((diaData, index) => {
        if (this.diasSemana[index] && diaData.ordens) {
          this.diasSemana[index].ordens = diaData.ordens.map(ordemRef => {
            const ordem = this.ordens.find(o => o.id === ordemRef.id);
            if (ordem) {
              return { ...ordem, posicaoNoDia: ordemRef.posicao };
            }
            return null;
          }).filter(Boolean);
        }
      });
    },

    async salvarPlanejamento() {
      if (!this.temAlteracoes) {
        this.mostrarNotificacaoInfo('Nenhuma alteração para salvar');
        return;
      }

      this.salvandoPlanejamento = true;
      try {
        const chaveSemana = this.getChaveSemana();
        const planejamento = {
          semana: chaveSemana,
          dias: this.diasSemana.map(dia => ({
            data: dia.data.toISOString(),
            ordens: dia.ordens.map((ordem, index) => ({
              id: ordem.id,
              posicao: index
            }))
          }))
        };

        const response = await producaoService.salvarPlanejamentoSemana(planejamento);

        if (response.status === 'sucesso') {
          // Atualizar semana alocada nas ordens
          this.diasSemana.forEach(dia => {
            dia.ordens.forEach(ordem => {
              ordem.semanaAlocada = chaveSemana;
              ordem.diaAlocado = dia.data;
            });
          });

          this.salvarPlanejamentoOriginal();
          this.atualizarOrdensDisponiveis();

          this.$emit('salvar-planejamento', planejamento);
          this.mostrarNotificacaoSucesso('Planejamento salvo com sucesso!');
        } else {
          throw new Error(response.message || 'Erro ao salvar planejamento');
        }

      } catch (error) {
        console.error('Erro ao salvar planejamento:', error);
        this.mostrarNotificacaoErro(
          error.message || 'Erro ao salvar planejamento'
        );
      } finally {
        this.salvandoPlanejamento = false;
      }
    },

    gerarPlanejamentoAtual() {
      return {
        semana: this.getChaveSemana(),
        dias: this.diasSemana.map(dia => ({
          data: dia.data.toISOString(),
          ordens: dia.ordens.map((ordem, index) => ({
            id: ordem.id,
            posicao: index
          }))
        }))
      };
    },

    salvarPlanejamentoOriginal() {
      this.planejamentoOriginal = JSON.parse(JSON.stringify(this.gerarPlanejamentoAtual()));
    },

    async semanaAnterior() {
      // Verificar se há alterações não salvas
      if (this.temAlteracoes && !confirm('Existem alterações não salvas. Deseja continuar mesmo assim?')) {
        return;
      }

      const novaSemana = new Date(this.semanaAtual);
      novaSemana.setDate(novaSemana.getDate() - 7);
      this.semanaAtual = novaSemana;

      // Recarregar todos os dados para a nova semana
      this.loading = true;
      try {
        console.log('📅 Mudando para semana anterior:', this.getChaveSemana());

        // Recarregar ordens e planejamento da nova semana
        await this.carregarOrdensDisponiveis();
        this.gerarDiasSemana();
        await this.carregarPlanejamentoSemana();

      } catch (error) {
        console.error('❌ Erro ao mudar para semana anterior:', error);
        this.mostrarNotificacaoErro('Erro ao carregar dados da semana anterior');
      } finally {
        this.loading = false;
      }
    },

    async proximaSemana() {
      // Verificar se há alterações não salvas
      if (this.temAlteracoes && !confirm('Existem alterações não salvas. Deseja continuar mesmo assim?')) {
        return;
      }

      const novaSemana = new Date(this.semanaAtual);
      novaSemana.setDate(novaSemana.getDate() + 7);
      this.semanaAtual = novaSemana;

      // Recarregar todos os dados para a nova semana
      this.loading = true;
      try {
        console.log('📅 Mudando para próxima semana:', this.getChaveSemana());

        // Recarregar ordens e planejamento da nova semana
        await this.carregarOrdensDisponiveis();
        this.gerarDiasSemana();
        await this.carregarPlanejamentoSemana();

      } catch (error) {
        console.error('❌ Erro ao mudar para próxima semana:', error);
        this.mostrarNotificacaoErro('Erro ao carregar dados da próxima semana');
      } finally {
        this.loading = false;
      }
    },

    onOrdemAdicionada(event, dia) {
      const ordem = event.item._underlying_vm_;
      if (ordem) {
        // Primeiro abrir o modal para seleção de setores
        this.abrirModalSetores(ordem, dia);

        // Estes valores serão definidos após confirmar a seleção
        // ordem.diaAlocado = dia.data;
        // ordem.semanaAlocada = this.getChaveSemana();
      }
      this.onPlanejamentoAlterado();
    },

    onOrdemRemovida(event, dia) {
      this.onPlanejamentoAlterado();
    },

    onPlanejamentoAlterado() {
      // Método chamado sempre que o planejamento é alterado
      // Pode ser usado para validações em tempo real
    },

    removerOrdemDoDia(ordem, dia) {
      const index = dia.ordens.findIndex(o => o.id === ordem.id);
      if (index !== -1) {
        dia.ordens.splice(index, 1);

        // Remover alocação da ordem
        delete ordem.diaAlocado;
        delete ordem.semanaAlocada;

        // Adicionar de volta às ordens disponíveis se não estiver lá
        if (!this.ordensDisponiveis.find(o => o.id === ordem.id)) {
          this.ordensDisponiveis.push(ordem);
        }

        this.onPlanejamentoAlterado();
      }
    },

    clonarOrdem(ordem) {
      return { ...ordem };
    },

    calcularCapacidadeDia(dia) {
      // Calcular capacidade baseada no número e complexidade das ordens
      let capacidade = 0;

      dia.ordens.forEach(ordem => {
        // Capacidade base por ordem (pode ser ajustada conforme a complexidade)
        let capacidadeOrdem = 20; // 20% por ordem padrão

        // Ajustar baseado na quantidade
        if (ordem.quantidadeTotal > 1000) capacidadeOrdem += 10;
        if (ordem.quantidadeTotal > 2000) capacidadeOrdem += 10;

        // Ajustar baseado na urgência
        if (ordem.urgente) capacidadeOrdem += 5;
        if (ordem.atrasada) capacidadeOrdem += 10;

        capacidade += capacidadeOrdem;
      });

      return Math.round(capacidade);
    },

    getCorCapacidade(capacidade) {
      if (capacidade > 100) return 'error';
      if (capacidade > 80) return 'warning';
      if (capacidade > 60) return 'info';
      return 'success';
    },

    getCorStatusSetor(status) {
      if (!status) return 'grey';

      switch (status.toLowerCase()) {
        case 'finalizado':
          return 'success';
        case 'pendente':
          return 'warning';
        case 'em_andamento':
          return 'info';
        case 'sem':
          return 'grey';
        case 'atrasado':
          return 'error';
        default:
          return 'grey';
      }
    },

    getInicioSemana(data) {
      const inicio = new Date(data);
      const dia = inicio.getDay();
      const diff = inicio.getDate() - dia + (dia === 0 ? -6 : 1); // Segunda-feira
      inicio.setDate(diff);
      inicio.setHours(0, 0, 0, 0);
      return inicio;
    },

    getFimSemana(data) {
      const inicio = this.getInicioSemana(data);
      const fim = new Date(inicio);
      fim.setDate(inicio.getDate() + 6);
      fim.setHours(23, 59, 59, 999);
      return fim;
    },

    getChaveSemana() {
      const inicio = this.getInicioSemana(this.semanaAtual);
      return `${inicio.getFullYear()}-W${this.getNumeroSemana(inicio)}`;
    },

    getNumeroSemana(data) {
      const inicio = new Date(data.getFullYear(), 0, 1);
      const dias = Math.floor((data - inicio) / (24 * 60 * 60 * 1000));
      return Math.ceil((dias + inicio.getDay() + 1) / 7);
    },

    ehHoje(data) {
      const hoje = new Date();
      return data.toDateString() === hoje.toDateString();
    },

    ehFimDeSemana(data) {
      const dia = data.getDay();
      return dia === 0 || dia === 6; // Domingo ou Sábado
    },

    formatarDia(data) {
      return data.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit'
      });
    },

    // Métodos de notificação
    mostrarNotificacaoSucesso(mensagem) {
      this.mensagemNotificacao = mensagem;
      this.tipoNotificacao = 'success';
      this.mostrarNotificacao = true;
    },

    mostrarNotificacaoErro(mensagem) {
      this.mensagemNotificacao = mensagem;
      this.tipoNotificacao = 'error';
      this.mostrarNotificacao = true;
    },

    mostrarNotificacaoInfo(mensagem) {
      this.mensagemNotificacao = mensagem;
      this.tipoNotificacao = 'info';
      this.mostrarNotificacao = true;
    }
  }
}
</script>

<style scoped>
.planejamento-semanal {
  min-height: 600px;
}

.bg-gradient-success {
  background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
}

.ordens-disponiveis-container {
  height: 600px;
  overflow-y: auto;
}

.ordens-disponiveis {
  min-height: 100px;
}

.semana-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  height: 600px;
  border: 1px solid #e0e0e0;
}

.dia-coluna {
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  background: white;
}

.dia-coluna:last-child {
  border-right: none;
}

.dia-hoje {
  background: rgba(25, 118, 210, 0.05);
}

.dia-fim-semana {
  background: rgba(158, 158, 158, 0.05);
}

.dia-header {
  padding: 12px 8px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  text-align: center;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
}

.dia-conteudo {
  flex: 1;
  padding: 8px;
  overflow-y: auto;
}

.dia-footer {
  padding: 8px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
}

.drop-zone {
  min-height: 100%;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.drop-zone-empty {
  border: 2px dashed #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drop-zone-over-capacity {
  background: rgba(244, 67, 54, 0.05);
  border: 2px dashed #f44336;
}

.drop-placeholder {
  text-align: center;
  padding: 20px;
  color: #9e9e9e;
}

/* Animações de drag and drop */
.sortable-ghost {
  opacity: 0.5;
  background: #f5f5f5;
  transform: rotate(2deg);
}

.sortable-chosen {
  transform: scale(1.02);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.sortable-drag {
  transform: rotate(5deg);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.gap-1>*+* {
  margin-left: 4px;
}

.gap-2>*+* {
  margin-left: 8px;
}

/* Responsividade */
@media (max-width: 1200px) {
  .semana-grid {
    grid-template-columns: repeat(4, 1fr);
    height: auto;
  }

  .dia-coluna {
    min-height: 200px;
  }
}

@media (max-width: 768px) {
  .semana-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .ordens-disponiveis-container {
    height: 400px;
  }
}

@media (max-width: 600px) {
  .semana-grid {
    grid-template-columns: 1fr;
  }

  .dia-coluna {
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }

  .dia-coluna:last-child {
    border-bottom: none;
  }

  /* Modal de Setores */
  .ordem-info {
    background: #f5f5f5;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 20px;
  }

  .ordem-info h3 {
    margin: 0 0 10px 0;
    color: #1976d2;
  }

  .ordem-info p {
    margin: 5px 0;
    color: #333;
  }

  .setores-selection h4 {
    margin-bottom: 20px;
    color: #333;
  }

  .setores-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 15px;
    margin-bottom: 20px;
  }

  .setor-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid transparent;
  }

  .setor-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .setor-card.selected {
    border-color: #1976d2;
    background: #e3f2fd;
  }

  .setor-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }

  .setor-header strong {
    color: #333;
  }

  .setor-info {
    color: #666;
    font-size: 12px;
  }

  .selected-setores {
    margin-top: 10px;
  }

  /* Responsividade */
  @media (max-width: 768px) {
    .setores-grid {
      grid-template-columns: 1fr;
    }
  }



}

/* ✅ CONTAINER PRINCIPAL */
.setores-grid-clean {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
  padding: 8px;
}

/* ✅ CARD BASE */
.setor-card-clean {
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  background: white;
  cursor: pointer;
}

.setor-card-clean:hover:not(.setor-disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: #3b82f6;
}

.setor-card-clean.selected {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.setor-card-clean.setor-disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f8fafc;
}

.setor-card-clean.setor-sem-saldo {
  border-color: #f59e0b;
  background: #fffbeb;
}

/* ✅ HEADER LIMPO */
.setor-header-clean {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.setor-card-clean.selected .setor-header-clean {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
}

.header-main {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.setor-info {
  flex: 1;
}

.setor-titulo {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
  line-height: 1.2;
}

.setor-tags {
  display: flex;
  align-items: center;
  gap: 12px;
}

.setor-detalhes {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

/* ✅ STATUS BADGE */
.status-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.disponivel {
  background: #dcfce7;
  color: #166534;
}

.status-badge.indisponivel {
  background: #fee2e2;
  color: #991b1b;
}

/* ✅ CORPO DO CARD */
.setor-body-clean {
  padding: 20px;
}

/* ✅ INFORMAÇÕES */
.info-row-clean {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.info-item-clean {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f1f5f9;
  border-radius: 8px;
}

.info-text {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

/* ✅ MÉTRICAS SIMPLIFICADAS (SÓ 3) */
.metricas-clean {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.metrica-clean {
  text-align: center;
  padding: 16px 12px;
  border-radius: 12px;
  border: 2px solid transparent;
  transition: all 0.2s ease;
}

.metrica-clean:hover {
  transform: translateY(-1px);
}

.metrica-clean.planejada {
  background: #dbeafe;
  border-color: #3b82f6;
}

.metrica-clean.produzida {
  background: #dcfce7;
  border-color: #10b981;
}

.metrica-clean.pendente {
  background: #fef3c7;
  border-color: #f59e0b;
}

.metrica-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #64748b;
  margin-bottom: 4px;
}

.metrica-valor {
  display: block;
  font-size: 20px;
  font-weight: 800;
  color: #1e293b;
}

/* ✅ SEÇÃO DE QUANTIDADE */
.quantidade-clean {
  background: #f0f9ff;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #bae6fd;
  margin-bottom: 16px;
}

.limite-text {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
}

.botoes-rapidos {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  justify-content: center;
}

/* ✅ MENSAGEM DE ERRO */
.erro-message {
  margin-bottom: 16px;
}

/* ✅ FOOTER */
.setor-footer-clean {
  padding: 16px 20px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

/* ✅ RESPONSIVIDADE */
@media (max-width: 768px) {
  .setores-grid-clean {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .info-row-clean {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .metricas-clean {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .setor-header-clean {
    padding: 16px;
  }

  .setor-body-clean {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .header-main {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .status-badge {
    align-self: flex-end;
  }
}
</style>